import moment from 'moment-timezone';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useBounds } from 'hooks/useBounds';
import { useCurrencyFormatter, getDisplayUnitPrice } from 'hooks/useCurrencyFormatter';
import { ProductShape } from 'lib/util/productShape';
import { printPriceTitle } from 'lib/util/util';
import { getPromotionTitleText } from 'lib/util/promotionTextHelpers';
import { getDaysOfWeekText } from 'lib/util/getDaysOfWeekText';

interface Props {
  product: ProductShape;
}

export const PromotionDetails = ({ product }: Props) => {
  const { i18n, t } = useTranslation();

  const [promoListRef, promoListRefBounds] = useBounds<HTMLUListElement>();
  const [viewMore, setViewMore] = React.useState<boolean>(false);

  const locale = i18n ? i18n.language : '';

  const shouldTruncate = promoListRefBounds.height > 600;

  const containerDivClasses = ['c-promotion'];
  const buttonDivClasses = ['c-promotion__more'];
  if (shouldTruncate) {
    containerDivClasses.push(viewMore ? 'is-open' : 'is-close');
    buttonDivClasses.push(viewMore ? 'is-open' : 'is-close');
  }

  const currencyFormatter = useCurrencyFormatter();

  if (product.promotionSummaries.length === 0) {
    return null;
  }

  return (
    <div className={containerDivClasses.join(' ')}>
      <ul ref={promoListRef} className="c-promotion__list">
        {product.promotionSummaries.map((promotion, idx) => {
          const participationDaysOfWeekText = getDaysOfWeekText(
            promotion.participationDaysOfWeek,
            t
          );

          return (
            <li className="c-promotion__list__item" key={idx}>
              <div className="c-promotion__list__item__header">
                <p className="c-promotion__list__item__header__label">
                  <img alt="special" src="/static/images/ic_special.svg" />
                  {t('Campaign')}
                </p>
                <p className="c-promotion__list__item__header__ttl">
                  {getPromotionTitleText(promotion, t, locale, currencyFormatter)}
                </p>
              </div>
              <div className="c-promotion__list__item__body">
                {/*
                <p className="c-promotion__list__item__body__description">
                </p>
                */}
                <div className="c-promotion__list__item__body__period">
                  {promotion.participationDateRanges.length > 0 && (
                    <p className="maincolor-txt">
                      {t('Participation Date: {{participationDate}}', {
                        participationDate: `${promotion.participationDateRanges
                          .map(
                            (dateRange) =>
                              `${moment(dateRange.startDate)
                                .locale(locale)
                                .format('ll')} ~ ${moment(dateRange.endDate)
                                .locale(locale)
                                .format('ll')}`
                          )
                          .join('; ')}${
                          participationDaysOfWeekText ? ` (${participationDaysOfWeekText})` : ''
                        }`,
                      })}
                    </p>
                  )}
                  {(promotion.bookedDateLocalFrom || promotion.bookedDateLocalTo) && (
                    <p className="maincolor-txt">
                      {t('Booked Date: {{bookedDateFrom}} ~ {{bookedDateTo}}', {
                        bookedDateFrom: moment(promotion.bookedDateLocalFrom)
                          .locale(locale)
                          .format('ll'),
                        bookedDateTo: moment(promotion.bookedDateLocalTo)
                          .locale(locale)
                          .format('ll'),
                      })}
                    </p>
                  )}
                </div>
                <ul className="c-promotion__list__item__body__target">
                  {promotion.prices
                    .filter((price) => price.amountAfterChargeOrDiscount !== price.amount)
                    .map((price) => {
                      return (
                        <li
                          className="c-promotion__list__item__body__target__item"
                          key={price.unitTitle}
                        >
                          <p className="c-promotion__list__item__body__target__item__ttl">
                            {printPriceTitle(price, t)}
                          </p>
                          <div className="c-promotion__list__item__body__target__item__main">
                            <div className="c-promotion__list__item__body__target__item__main__discountprice">
                              {(price.deltaPercent || price.deltaAmount) &&
                                (price.deltaType === 'CHARGE' ? (
                                  <p className="percent">
                                    {t('+{{charge}}', {
                                      charge:
                                        price.deltaPercent ||
                                        currencyFormatter.format(price.deltaAmount),
                                    })}
                                  </p>
                                ) : (
                                  <p className="percent">
                                    {t('{{discount}} OFF', {
                                      discount:
                                        price.deltaPercent ||
                                        currencyFormatter.format(price.deltaAmount),
                                    })}
                                  </p>
                                ))}
                              <p className="price">
                                {getDisplayUnitPrice(
                                  currencyFormatter.format(price.amountAfterChargeOrDiscount),
                                  price
                                )}
                              </p>
                            </div>
                            {price.amountAfterChargeOrDiscount !== price.amount && (
                              <p className="c-promotion__list__item__body__target__item__main__normalprice">
                                {currencyFormatter.format(price.amount)}
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </li>
          );
        })}
      </ul>
      {shouldTruncate && (
        <div className={buttonDivClasses.join(' ')}>
          <button
            type="button"
            className="c-promotion__more__btn maincolor-txt maincolor-border"
            onClick={() => setViewMore(!viewMore)}
          >
            {viewMore ? t('Show Less') : t('Show More')}
          </button>
        </div>
      )}
    </div>
  );
};
